<template>
  <div
    class="recommendations-carousel critical-component-clear"
  >
    <product-card-carousel
      :carousel-data="recommendedProducts"
      :page-type="liquid.pageType"
      :title="title"
      :show-discount="showDiscount"
    />
  </div>
</template>

<script>

/**
 * Vue: Recommendations carousel (recommendations-carousel)
 * -----------------------------------------------------------------------------
 * Carousel of products.
 *
 * @param {Array} carouselData - Array of formatted products.
 * @param {Object} liquid - Shopify Liquid settings.
 * @param {Object} tagalys - Tagalys settings.
 *
 */
import { mapState } from 'vuex'
import { onTagalysReady } from '~/apis/tagalys'
import { formatProduct } from '~/format/format-tagalys'

import ProductCardCarousel from '~async/product-card-carousel/product-card-carousel'

export default {
  name: 'RecommendationsCarousel',

  components: {
    ProductCardCarousel,
  },

  props: {
    carouselData: {
      type: Array,
      default: () => [],
    },
    liquid: {
      type: Object,
      default: () => ({
        limit: 8,
        pageType: 'index',
        productId: false,
        title: '',
      }),
    },
    tagalys: {
      type: Object,
      default: () => ({
        recommendationsId: '',
        useSmartWidgetPdp: false,
      }),
    },
  },

  data() {
    return {
      config: {
        arrows: true,
        autoplay: false,
        drag: true,
        flickPower: 200,
        flickMaxPages: 1,
        gap: '1rem',
        mediaQuery: 'min',
        padding: { left: '1rem', right: '35%' },
        pagination: false,
        perMove: 1,
        perPage: 1,
        start: 0,
        type: 'slide',
      },
      currentIndex: 0,
      recommendedProducts: [],
      tagalysConfig: {
        apiConnector: null,
        params: {},
      },
      title: this.liquid.title,
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
    }),

    showDiscount() {
      return this.liquid.showDiscount || 'show'
    },

    /**
     * Return if current page is a product page.
     * @returns {Boolean}
     */
    isPdp() {
      return this.liquid.pageType === 'product'
    },
  },

  mounted() {
    this.setRecommendedProducts()
  },

  methods: {

    /**
     * Handle events when Splide has moved.
     * @param {Integer} newIndex - New splide index.
     */
    handleSplideMoved(_, newIndex) {
      if (newIndex === this.currentIndex) {
        return
      }

      this.currentIndex = newIndex
    },

    /**
     * Call Tagalys ready file to run config and callback function.
     */
    initialiseTagalys() {
      if (!this.tagalys.recommendationsId) {
        return
      }

      onTagalysReady(this.callTagalys)
    },

    /**
     * Set recommended products to liquid carousel data, or initialise Tagalys
     * if an ID is present.
     */
    setRecommendedProducts() {
      if (!this.tagalys.recommendationsId) {
        this.recommendedProducts = this.carouselData
        return
      }

      this.initialiseTagalys()
    },

    /**
     * Query Tagalys for products based on ID in Shopify section settings.
     * - If there are responses, re-assign `recommendedProducts` for the
     *   product card carousel component.
     */
    async callTagalys() {
      this.setTagalysConfig()

      await this.$nextTick().then(() => {
        this.tagalysConfig.apiConnector.call({
          params: this.tagalysConfig.params,
          onSuccess: (response) => {
            if (response.products.length <= 0) {
              this.recommendedProducts = this.carouselData

              return
            }

            this.recommendedProducts = response.products.map((tagalysProduct) => {
              return formatProduct(tagalysProduct)
            })

            this.title = response.name || this.liquid.title
          },
          onFailure: (error) => {
            this.recommendedProducts = this.carouselData
            window.console.error('Tagalys error', error)
          },
        })
      })
    },

    /**
     * Set Tagalys config based on the current page.
     */
    setTagalysConfig() {
      const useRecommendationsWidget = this.isPdp && !this.tagalys.useSmartWidgetPdp

      this.tagalysConfig.apiConnector = useRecommendationsWidget
        ? window.Tagalys.APIConnector.Recommendations
        : window.Tagalys.APIConnector.SmartWidget

      const widgetParams = useRecommendationsWidget
        ? {
          productId: this.liquid.productId,
          recommendationId: this.tagalys.recommendationsId,
        }
        : { widgetId: this.tagalys.recommendationsId }

      this.tagalysConfig.params = {
        ...widgetParams,
        limit: this.liquid.limit,
      }
    },
  },
}
</script>

<style lang="scss">
@import '@/components/splide';
@import './recommendations-carousel';
</style>
